// app/(app)/layout.tsx
"use client"

import "@/styles/globals.css"

import { useEffect } from "react"
import Script from "next/script"
import { FeatureFlagsProvider } from "@/contexts/feature-flags"
import { LanguageProvider, Locale } from "@/contexts/language"
import { OrganizationProvider } from "@/contexts/organization"
import { RoleProvider } from "@/contexts/roles"
import {
  ConsumersSocketProvider,
  MainApiSocketProvider,
} from "@/contexts/websocket"
import { ErrorBoundary } from "@sentry/react"
import AOS from "aos"
import Cookie from "js-cookie"
import { useSession } from "next-auth/react"
import { Toaster as RedHotToaster } from "react-hot-toast"
import { Client } from "react-hydration-provider"

import { fontSans } from "@/lib/fonts"
import { cn } from "@/lib/utils"
import { useSiteConfigEffects } from "@/hooks/use-site-config-effects"
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import { SiteAppsNav } from "@/components/site-apps-nav"
import { SiteHeader } from "@/components/site-header"
import { TailwindIndicator } from "@/components/tailwind-indicator"
import { ThemeProvider } from "@/components/theme-provider"

import "aos/dist/aos.css"

interface RootLayoutProps {
  children: React.ReactNode
}

function FallbackComponent() {
  return <div>An error has occured</div>
}

export default function RootLayout({ children }: RootLayoutProps) {
  const initialLocale = Cookie.get("locale") || null
  return (
    <ErrorBoundary fallback={FallbackComponent} showDialog>
      <RoleProvider>
        <MainApiSocketProvider>
          <LanguageProvider initialLocale={initialLocale as Locale}>
            <OrganizationProvider>
              <FeatureFlagsProvider>
                <LayoutContent>
                  <Client>{children}</Client>
                </LayoutContent>
              </FeatureFlagsProvider>
            </OrganizationProvider>
          </LanguageProvider>
        </MainApiSocketProvider>
      </RoleProvider>
    </ErrorBoundary>
  )
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const { data: clientSession, status } = useSession()
  useSiteConfigEffects()
  const isUserLoggedIn = !!clientSession?.user
  useEffect(() => {
    AOS.init()
  })
  return (
    <>
      <html lang="es" suppressHydrationWarning>
        {isUserLoggedIn && (
          <head>
            <Script id="x42-script">
              {`
              window.x42Settings = { widget_id: 'ae5e24e7-7662-4ecc-b04e-cb4a30c8df5d' };
              !function(){var t=window,e=document,a=function(){var t=e.createElement("script");t.type="text/javascript",t.async=!0,t.src="https://app.42x.ai/static/js/42-widget.js";var a=e.getElementsByTagName("script")[0];a.parentNode.insertBefore(t,a)};"complete"===document.readyState?a():t.attachEvent?t.attachEvent("onload",a):t.addEventListener("load",a,!1)}();
            `}
            </Script>
          </head>
        )}
        <body
          className={cn(
            "min-h-screen bg-background font-sans text-sm antialiased",
            fontSans.variable
          )}
        >
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
            <TooltipProvider>
              <div className="relative flex h-screen flex-col">
                {isUserLoggedIn && <SiteHeader />}
                <div className="flex flex-wrap w-full">
                  {isUserLoggedIn && <SiteAppsNav />}
                  <div className="h-full w-[calc(100%_-_80px)]">
                    <div className="h-full flex-1 self-stretch">{children}</div>
                  </div>
                </div>
              </div>
            </TooltipProvider>
            <TailwindIndicator />
          </ThemeProvider>
          <Toaster />
          <RedHotToaster />
        </body>
      </html>
    </>
  )
}
